import React, { useState, useMemo } from "react";
import "./calculateTask3LG.less";
import dayjs from "dayjs";
import { Tooltip, Select, AutoComplete } from "antd";
import { formatBySig, resolveSig, formatErrorPercent, isNumValue } from "@/utils/numberFormat";

const phaseMap: { [key: string]: string } = {
  V: "气相",
  L: "液相",
  VL: "气液混合",
};

const temperatureUnits = ["℃", "K"];
const pressureUnits = ["Pa", "bar", "KPa", "MPa", "atm"];

interface Props {
  streams: any[];
  compareItems: any[];
  showAllData?: boolean;
  input?: any;
  streamDescMap?: Record<string, string>;
  streamNodeMap?: Record<string, { fromName: string; toName: string }>;
  elementDescMap?: Record<string, string>;
  tempUnitProp?: string;
  pressUnitProp?: string;
  //筛选源和目标
  sourceFilter?: string;
  setSourceFilter?: (v: string) => void;
  sourceOptions?: string[];
  targetFilter?: string;
  setTargetFilter?: (v: string) => void;
  targetOptions?: string[];
}

const ResultStreamDataTable: React.FC<Props> = ({ streams, compareItems, showAllData = true, input, streamDescMap = {}, streamNodeMap = {}, elementDescMap = {}, tempUnitProp, pressUnitProp, sourceFilter = "", setSourceFilter, sourceOptions = [], targetFilter = "", setTargetFilter, targetOptions = [] }) => {
  const [internalTempUnit, setInternalTempUnit] = useState<string>("℃");
  const [internalPressUnit, setInternalPressUnit] = useState<string>("MPa");
  // 液相体积流量单位切换（m³/h / L/h）
  const [selectVolFlow, setSelectVolFlow] = useState("volFlowM3h");
  const tempUnit = tempUnitProp ?? internalTempUnit;
  const pressUnit = pressUnitProp ?? internalPressUnit;

  // 从 input 中提取材料数据（含 MW），用于对比摩尔流量计算
  const inputMaterials: any[] = useMemo(() => {
    const streamWithMats = input?.inputs?.streams?.find((item: any) => item.materials?.length);
    return streamWithMats?.materials ?? [];
  }, [input]);

  // 数值展示：按有效数字格式化（非 0 值不会显示成 0，极小/极大值自动用科学计数法），sig 由字段内自适应精度决定
  const fmt = (v: any, sig: number = 6) => (isNumValue(v) ? formatBySig(v, sig) : "—");

  // 计算误差百分比
  const calculateErrorPercent = (compareValue: number, calcValue: number): string => {
    // 判断设计值/实际值/任务值和计算值是否都为 0
    if (compareValue === 0 && calcValue === 0) {
      return "0%";
    }
    // 如果计算值不为 0 但设计值/实际值/任务值为 0
    if (compareValue === 0 && calcValue !== 0) {
      return "100%";
    }
    // 正常计算误差百分比：(计算值 - 设计值/实际值/任务值) ÷ 设计值/实际值/任务值
    const errorPercent = ((calcValue - compareValue) / compareValue) * 100;
    // 非 0 但极小的误差显示 <0.01%，避免出现"0.00% 却被标红"的矛盾
    return formatErrorPercent(errorPercent);
  };

  // 判断当前值与对比值是否有差异（仅高亮有差异的对比单元格，不整行高亮）
  const valuesDiffer = (current: any, compare: any): boolean => {
    // 缺失值：- / — / null / undefined
    const curMissing = current === "-" || current === "—" || current === null || current === undefined;
    const cmpMissing = compare === "-" || compare === "—" || compare === null || compare === undefined;
    // 任一侧缺失（页面显示为 "-"/"—"）都不算差异，不标红
    if (curMissing || cmpMissing) return false;
    const curStr = String(current);
    const cmpStr = String(compare);
    const curNum = parseFloat(curStr);
    const cmpNum = parseFloat(cmpStr);
    const curIsNum = !isNaN(curNum);
    const cmpIsNum = !isNaN(cmpNum);
    if (curIsNum && cmpIsNum) {
      // 数值比较：相对容差比较，忽略浮点噪声（相对误差 < 0.0000001% 视为相等，与显示一致）
      if (curNum === cmpNum) return false;
      const tolerance = Math.max(Math.abs(curNum), Math.abs(cmpNum)) * 1e-9;
      return Math.abs(curNum - cmpNum) > tolerance;
    }
    // 非数值：字符串直接比较
    return curStr !== cmpStr;
  };

  // 获取温度值（适配单位）
  const getT = (s: any) => {
    if (!s) return null;
    return tempUnit === "℃" ? s.T_C : s.T_K;
  };

  // 获取对比流股的温度值
  const getCompareT = (cs: any) => {
    if (!cs?.stream?.params) return null;
    const t = cs.stream.params.t;
    const unit = cs.stream.params.t_unit || "C";
    if (tempUnit === "℃") return unit === "C" ? t : t - 273.15;
    return unit === "K" ? t : t + 273.15;
  };

  // 获取压力值（适配单位）
  const getP = (s: any) => s?.[`P_${pressUnit}`];

  // 获取对比流股的压力值
  const getCompareP = (cs: any) => {
    if (!cs?.stream?.params) return null;
    // 缺失（null）时返回 null，显示缺失标记，不用 0 替换
    const p = cs.stream.params.p ?? null;
    if (p === null) return null;
    const unit = cs.stream.params.p_unit || "MPa";
    let convertedP = p;
    if (unit === "MPa") convertedP = p * 1e6;
    if (unit === "bar") convertedP = p * 1e5;
    if (unit === "KPa") convertedP = p * 1000;
    if (unit === "atm") convertedP = p * 101325;
    if (unit === "Pa") convertedP = p;
    switch (pressUnit) {
      case "Pa":
        return convertedP;
      case "bar":
        return convertedP / 1e5;
      case "KPa":
        return convertedP / 1000;
      case "MPa":
        return convertedP / 1e6;
      case "atm":
        return convertedP / 101325;
      default:
        return convertedP;
    }
  };

  // 计算对比流股的平均摩尔质量
  const calcAvgMW = (streamData: any, materials: any[]) => {
    if (!streamData?.materials || !materials) return null;
    let total = 0;
    streamData.materials.forEach((mat: any, idx: number) => {
      total += (mat.molar_frac || 0) * (materials[idx]?.MW || 1);
    });
    return total;
  };

  const compareCount = compareItems.length;

  // ========== 行列过滤逻辑 ==========
  const fieldKeys = ["温度", "压力", "摩尔流量", "质量流量", "体积流量（Nm³/h）", "体积流量", "气化率", "焓"] as const;

  const getCalcVal = (s: any, field: string): any => {
    switch (field) {
      case "温度": return getT(s);
      case "压力": return getP(s);
      case "摩尔流量": return s.f_mol_per_h / 1000;
      case "质量流量": return s.f_kg_per_h;
      case "体积流量（Nm³/h）": return s.f_nm3_per_h;
      case "体积流量": return selectVolFlow === "volFlowM3h" ? s.f_m3_per_h : s.f_l_per_h;
      case "气化率": return s.vf;
      case "焓": return s.H;
      default: return 0;
    }
  };

  const getCompareVal = (s: any, field: string, cs: any): any => {
    if (!cs?.stream) return null;
    switch (field) {
      case "温度": return getCompareT(cs);
      case "压力": return getCompareP(cs);
      case "摩尔流量": {
        const massFlow = cs.stream.params?.f || 0;
        const avgMW = calcAvgMW(cs.stream, inputMaterials);
        return avgMW ? ((massFlow / avgMW) * 1000) / 1000 : null;
      }
      case "质量流量": return cs?.stream?.params?.f;
      case "体积流量（Nm³/h）": return cs?.stream?.f_nm3_per_h ?? null;
      case "体积流量": return selectVolFlow === "volFlowM3h" ? (cs?.stream?.f_m3_per_h ?? null) : (cs?.stream?.f_l_per_h ?? null);
      case "气化率": return cs?.stream?.vf ?? null;
      case "焓": return cs?.stream?.H ?? null;
      default: return null;
    }
  };

  const isZeroOrInvalid = (val: any): boolean => {
    if (val === null || val === undefined || val === "—") return true;
    return Number(val) === 0;
  };

  const { visibleStreams, visibleFields } = useMemo(() => {
    if (!streams.length) return { visibleStreams: streams, visibleFields: [...fieldKeys] };
    if (showAllData) return { visibleStreams: streams, visibleFields: [...fieldKeys] };

    const vs = streams.filter((s) => {
      return !fieldKeys.every((fk) => {
        const cv = getCalcVal(s, fk);
        if (!isZeroOrInvalid(cv)) return false;
        for (let i = 0; i < compareCount; i++) {
          const cv2 = getCompareVal(s, fk, s.compareData?.[i]);
          if (!isZeroOrInvalid(cv2)) return false;
        }
        return true;
      });
    });

    const vf = fieldKeys.filter((fk) => {
      return !streams.every((s) => {
        const cv = getCalcVal(s, fk);
        if (!isZeroOrInvalid(cv)) return false;
        for (let i = 0; i < compareCount; i++) {
          const cv2 = getCompareVal(s, fk, s.compareData?.[i]);
          if (!isZeroOrInvalid(cv2)) return false;
        }
        return true;
      });
    });

    return { visibleStreams: vs, visibleFields: vf };
  }, [streams, showAllData, compareCount, tempUnit, pressUnit, selectVolFlow]);

  const fieldLabelMap: Record<string, string> = {
    "温度": `温度(${tempUnit})`,
    "压力": `压力(${pressUnit})`,
    "摩尔流量": "摩尔流量(kmol/h)",
    "质量流量": "质量流量(kg/h)",
    "体积流量（Nm³/h）": "气相体积流量（Nm³/h）",
    "体积流量": "液相体积流量",
    "气化率": "气化率",
    "焓": "焓(J/mol)",
  };

  return (
    <div className="result-stream-data-table-container">
      <div className="unit-selector-container">
        <div className="unit-selector" style={{ marginTop: 5 }}>
          <span className="unit-selector-label">流股数据：</span>
          <span style={{ fontSize: 14, whiteSpace: "nowrap" }}>源：</span>
          <AutoComplete
            value={sourceFilter}
            onChange={setSourceFilter}
            options={sourceOptions.map((n) => ({ value: n }))}
            placeholder="请输入源设备"
            allowClear
            style={{ width: 120 }}
            size="small"
            filterOption={(inputValue, option) => (option?.value ?? "").includes(inputValue)}
          />
          <span style={{ fontSize: 14, whiteSpace: "nowrap" }}>目标：</span>
          <AutoComplete
            value={targetFilter}
            onChange={setTargetFilter}
            options={targetOptions.map((n) => ({ value: n }))}
            placeholder="请输入目标设备"
            allowClear
            style={{ width: 130 }}
            size="small"
            filterOption={(inputValue, option) => (option?.value ?? "").includes(inputValue)}
          />
          {/* <span className="unit-selector-label">温度单位：</span>
          <div className="unit-buttons">
            {temperatureUnits.map((u) => (
              <button
                key={u}
                className={`unit-button ${tempUnit === u ? "active" : ""}`}
                onClick={() => setInternalTempUnit(u)}
              >
                {u}
              </button>
            ))}
          </div>
        </div>
        <div className="unit-selector pressure-selector">
          <span className="unit-selector-label">压力单位：</span>
          <div className="unit-buttons">
            {pressureUnits.map((u) => (
              <button
                key={u}
                className={`unit-button ${pressUnit === u ? "active" : ""}`}
                onClick={() => setInternalPressUnit(u)}
              >
                {u}
              </button>
            ))}
          </div> */}
        </div>
      </div>

      <div className="table-container">
        <div className="table-scroll-wrapper">
          <table className="stream-data-table">
            <thead>
              <tr className="table-header">
                <th rowSpan={2}>流股</th>
                <th rowSpan={2}>流股描述</th>
                <th rowSpan={2}>源</th>
                <th rowSpan={2}>目标</th>
                <th rowSpan={2}>相态</th>
                {visibleFields.map((field) => (
                  <th key={field} colSpan={1 + compareCount}>
                    {field === "体积流量" ? (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                        液相体积流量
                        <Select
                          size="small"
                          value={selectVolFlow}
                          onChange={(v) => setSelectVolFlow(v)}
                          style={{ width: 70,marginLeft:10 }}
                          options={[
                            { value: "volFlowM3h", label: "m³/h" },
                            { value: "volFlowLh", label: "L/h" },
                          ]}
                        />
                      </span>
                    ) : (
                      fieldLabelMap[field]
                    )}
                  </th>
                ))}
              </tr>
              {compareCount > 0 && (
                <tr className="table-header">
                  {visibleFields.map((field) => (
                    <React.Fragment key={field}>
                      <th>当前计算值</th>
                      {compareItems.map((item, i) => (
                        <th key={i}>
                          {item.type === "task" 
                            ? item.values.name 
                            : item.type === "otherCalc"
                            ? `${item.values.name} (${item.created_at ? dayjs(item.created_at).add(8, "hours").format("YYYY-MM-DD HH:mm:ss") : ''})`
                            : item.type === "design" ? `设计值 (${item.id})` : (item.name || item.values?.name) || (item.type === "timeRange" ? (item.fullDay ? `运行数据 ${item.date}` : `运行数据 ${item.startTime} ~ ${item.endTime}`) : `运行数据 (${item.id})`)}
                        </th>
                      ))}
                    </React.Fragment>
                  ))}
                </tr>
              )}
            </thead>
            <tbody>
              {visibleStreams.length > 0 ? (
                visibleStreams.map((s, i) => {
                  const compareData = s.compareData || [];
                  return (
                    <tr key={i} className="table-row">
                      <td>{s.name}</td>
                      <td>{streamDescMap[s.name] || "—"}</td>
                      <td>
                        <Tooltip title={elementDescMap[streamNodeMap[s.name]?.fromName] || "暂无描述"}>
                          <span style={{ cursor: "pointer", borderBottom: "1px dashed #999" }}>{streamNodeMap[s.name]?.fromName || "—"}</span>
                        </Tooltip>
                      </td>
                      <td>
                        <Tooltip title={elementDescMap[streamNodeMap[s.name]?.toName] || "暂无描述"}>
                          <span style={{ cursor: "pointer", borderBottom: "1px dashed #999" }}>{streamNodeMap[s.name]?.toName || "—"}</span>
                        </Tooltip>
                      </td>
                      <td>{phaseMap[s.phase] ?? s.phase ?? "—"}</td>
                      {visibleFields.map((field) => {
                        const renderCell = () => {
                          switch (field) {
                            case "温度": {
                              const calcVal = getT(s);
                              // 收集该字段的计算值与全部对比值，取同一精度（有差异时提升），保证差异在显示上也能看出来
                              const compareVals = compareData.map((cs: any) => getCompareT(cs));
                              const sig = resolveSig([calcVal, ...compareVals].filter((v) => isNumValue(v)), valuesDiffer);
                              const rows = compareVals.map((compareVal: any) => {
                                const errorPercent = (compareVal !== null && compareVal !== 0)
                                  ? calculateErrorPercent(compareVal, calcVal)
                                  : (compareVal === 0 && calcVal === 0) ? "0.00%" : (calcVal !== 0 ? "100.00%" : "-");
                                return {
                                  compareVal,
                                  content: compareVal !== null ? `${fmt(compareVal, sig)}(${errorPercent})` : "—",
                                  diff: valuesDiffer(calcVal, compareVal),
                                };
                              });
                              // 该字段下任一对比值与计算值有差异，则该字段的计算值+所有对比值单元格标红
                              const hasDiff = rows.some((r) => r.diff);
                              return (
                                <>
                                  <td className={hasDiff ? "compare-cell-diff" : ""} title={String(calcVal)}>{fmt(calcVal, sig)}</td>
                                  {compareCount > 0 && rows.map((r, idx) => (
                                    <td key={idx} className={hasDiff ? "compare-cell-diff" : ""} title={String(r.compareVal)}>{r.content}</td>
                                  ))}
                                </>
                              );
                            }
                            case "压力": {
                              const calcVal = getP(s);
                              const compareVals = compareData.map((cs: any) => getCompareP(cs));
                              const sig = resolveSig([calcVal, ...compareVals].filter((v) => isNumValue(v)), valuesDiffer);
                              const rows = compareVals.map((compareVal: any) => {
                                const errorPercent = (compareVal !== null && compareVal !== 0)
                                  ? calculateErrorPercent(compareVal, calcVal)
                                  : (compareVal === 0 && calcVal === 0) ? "0.00%" : (calcVal !== 0 ? "100.00%" : "-");
                                return {
                                  compareVal,
                                  content: compareVal !== null ? `${fmt(compareVal, sig)}(${errorPercent})` : "—",
                                  diff: valuesDiffer(calcVal, compareVal),
                                };
                              });
                              const hasDiff = rows.some((r) => r.diff);
                              return (
                                <>
                                  <td className={hasDiff ? "compare-cell-diff" : ""} title={String(calcVal)}>{fmt(calcVal, sig)}</td>
                                  {compareCount > 0 && rows.map((r, idx) => (
                                    <td key={idx} className={hasDiff ? "compare-cell-diff" : ""} title={String(r.compareVal)}>{r.content}</td>
                                  ))}
                                </>
                              );
                            }
                            case "摩尔流量": {
                              const calcVal = s.f_mol_per_h / 1000;
                              // 先算出各对比项的摩尔流量（缺失为 null），再统一取精度
                              const compareVals = compareData.map((cs: any) => {
                                if (!cs?.stream) return null;
                                // 质量流量缺失（null/undefined）时直接显示 "—"，不用 0 计算
                                const fKg = cs.stream.params?.f;
                                if (fKg == null) return null;
                                const massFlow = cs.stream.params?.f || 0;
                                const avgMW = calcAvgMW(cs.stream, inputMaterials);
                                return avgMW ? ((massFlow / avgMW) * 1000) / 1000 : null;
                              });
                              const sig = resolveSig([calcVal, ...compareVals].filter((v) => isNumValue(v)), valuesDiffer);
                              const rows = compareVals.map((compareVal: any) => {
                                if (compareVal == null) return { compareVal, content: "—", diff: valuesDiffer(calcVal, null) };
                                const errorPercent = (compareVal !== null && compareVal !== 0)
                                  ? calculateErrorPercent(compareVal, calcVal)
                                  : (compareVal === 0 && calcVal === 0) ? "0.00%" : (calcVal !== 0 ? "100.00%" : "-");
                                return {
                                  compareVal,
                                  content: compareVal !== null ? `${fmt(compareVal, sig)}(${errorPercent})` : "—",
                                  diff: valuesDiffer(calcVal, compareVal),
                                };
                              });
                              const hasDiff = rows.some((r) => r.diff);
                              return (
                                <>
                                  <td className={hasDiff ? "compare-cell-diff" : ""} title={String(calcVal)}>{fmt(calcVal, sig)}</td>
                                  {compareCount > 0 && rows.map((r, idx) => (
                                    <td key={idx} className={hasDiff ? "compare-cell-diff" : ""} title={String(r.compareVal)}>{r.content}</td>
                                  ))}
                                </>
                              );
                            }
                            case "质量流量": {
                              const calcVal = s.f_kg_per_h;
                              const compareVals = compareData.map((cs: any) => cs?.stream?.params?.f);
                              const sig = resolveSig([calcVal, ...compareVals].filter((v) => isNumValue(v)), valuesDiffer);
                              const rows = compareVals.map((compareVal: any) => {
                                // 缺失（null/undefined）时只显示 "—"，不拼误差百分比
                                if (compareVal == null) return { compareVal, content: "—", diff: valuesDiffer(calcVal, null) };
                                const errorPercent = (compareVal !== 0)
                                  ? calculateErrorPercent(compareVal, calcVal)
                                  : (compareVal === 0 && calcVal === 0) ? "0.00%" : (calcVal !== 0 ? "100.00%" : "-");
                                return {
                                  compareVal,
                                  content: `${fmt(compareVal, sig)}(${errorPercent})`,
                                  diff: valuesDiffer(calcVal, compareVal),
                                };
                              });
                              const hasDiff = rows.some((r) => r.diff);
                              return (
                                <>
                                  <td className={hasDiff ? "compare-cell-diff" : ""} title={String(calcVal)}>{fmt(calcVal, sig)}</td>
                                  {compareCount > 0 && rows.map((r, idx) => (
                                    <td key={idx} className={hasDiff ? "compare-cell-diff" : ""} title={String(r.compareVal)}>{r.content}</td>
                                  ))}
                                </>
                              );
                            }
                            case "体积流量（Nm³/h）": {
                              const calcVal = s.f_nm3_per_h;
                              const compareVals = compareData.map((cs: any) => getCompareVal(s, "体积流量（Nm³/h）", cs));
                              const sig = resolveSig([calcVal, ...compareVals].filter((v) => isNumValue(v)), valuesDiffer);
                              const rows = compareVals.map((compareVal: any) => {
                                if (compareVal == null) return { compareVal, content: "—", diff: valuesDiffer(calcVal, null) };
                                const errorPercent = (compareVal !== 0)
                                  ? calculateErrorPercent(compareVal, calcVal)
                                  : (calcVal === 0 ? "0.00%" : "100.00%");
                                return {
                                  compareVal,
                                  content: `${fmt(compareVal, sig)}(${errorPercent})`,
                                  diff: valuesDiffer(calcVal, compareVal),
                                };
                              });
                              const hasDiff = rows.some((r) => r.diff);
                              return (
                                <>
                                  <td className={hasDiff ? "compare-cell-diff" : ""} title={String(calcVal)}>{fmt(calcVal, sig)}</td>
                                  {compareCount > 0 && rows.map((r, idx) => (
                                    <td key={idx} className={hasDiff ? "compare-cell-diff" : ""} title={String(r.compareVal)}>{r.content}</td>
                                  ))}
                                </>
                              );
                            }
                            case "体积流量": {
                              const calcVal = selectVolFlow === "volFlowM3h" ? s.f_m3_per_h : s.f_l_per_h;
                              const compareVals = compareData.map((cs: any) => getCompareVal(s, "体积流量", cs));
                              const sig = resolveSig([calcVal, ...compareVals].filter((v) => isNumValue(v)), valuesDiffer);
                              const rows = compareVals.map((compareVal: any) => {
                                if (compareVal == null) return { compareVal, content: "—", diff: valuesDiffer(calcVal, null) };
                                const errorPercent = (compareVal !== 0)
                                  ? calculateErrorPercent(compareVal, calcVal)
                                  : (calcVal === 0 ? "0.00%" : "100.00%");
                                return {
                                  compareVal,
                                  content: `${fmt(compareVal, sig)}(${errorPercent})`,
                                  diff: valuesDiffer(calcVal, compareVal),
                                };
                              });
                              const hasDiff = rows.some((r) => r.diff);
                              return (
                                <>
                                  <td className={hasDiff ? "compare-cell-diff" : ""} title={String(calcVal)}>{fmt(calcVal, sig)}</td>
                                  {compareCount > 0 && rows.map((r, idx) => (
                                    <td key={idx} className={hasDiff ? "compare-cell-diff" : ""} title={String(r.compareVal)}>{r.content}</td>
                                  ))}
                                </>
                              );
                            }
                            case "气化率": {
                              const calcVal = s.vf;
                              const compareVals = compareData.map((cs: any) => getCompareVal(s, "气化率", cs));
                              const sig = resolveSig([calcVal, ...compareVals].filter((v) => isNumValue(v)), valuesDiffer);
                              const rows = compareVals.map((compareVal: any) => {
                                if (compareVal == null) return { compareVal, content: "—", diff: valuesDiffer(calcVal, null) };
                                const errorPercent = (compareVal !== 0)
                                  ? calculateErrorPercent(compareVal, calcVal)
                                  : (calcVal === 0 ? "0.00%" : "100.00%");
                                return {
                                  compareVal,
                                  content: `${fmt(compareVal, sig)}(${errorPercent})`,
                                  diff: valuesDiffer(calcVal, compareVal),
                                };
                              });
                              const hasDiff = rows.some((r) => r.diff);
                              return (
                                <>
                                  <td className={hasDiff ? "compare-cell-diff" : ""} title={String(calcVal)}>{fmt(calcVal, sig)}</td>
                                  {compareCount > 0 && rows.map((r, idx) => (
                                    <td key={idx} className={hasDiff ? "compare-cell-diff" : ""} title={String(r.compareVal)}>{r.content}</td>
                                  ))}
                                </>
                              );
                            }
                            case "焓": {
                              const calcVal = s.H;
                              const compareVals = compareData.map((cs: any) => getCompareVal(s, "焓", cs));
                              const sig = resolveSig([calcVal, ...compareVals].filter((v) => isNumValue(v)), valuesDiffer);
                              const rows = compareVals.map((compareVal: any) => {
                                if (compareVal == null) return { compareVal, content: "—", diff: valuesDiffer(calcVal, null) };
                                const errorPercent = (compareVal !== 0)
                                  ? calculateErrorPercent(compareVal, calcVal)
                                  : (calcVal === 0 ? "0.00%" : "100.00%");
                                return {
                                  compareVal,
                                  content: `${fmt(compareVal, sig)}(${errorPercent})`,
                                  diff: valuesDiffer(calcVal, compareVal),
                                };
                              });
                              const hasDiff = rows.some((r) => r.diff);
                              return (
                                <>
                                  <td className={hasDiff ? "compare-cell-diff" : ""} title={String(calcVal)}>{fmt(calcVal, sig)}</td>
                                  {compareCount > 0 && rows.map((r, idx) => (
                                    <td key={idx} className={hasDiff ? "compare-cell-diff" : ""} title={String(r.compareVal)}>{r.content}</td>
                                  ))}
                                </>
                              );
                            }
                            default:
                              return null;
                          }
                        };
                        return <React.Fragment key={field}>{renderCell()}</React.Fragment>;
                      })}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={5 + visibleFields.length * (1 + compareCount)} className="empty-cell">请选择流股</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ResultStreamDataTable;